import axios from "axios";

/**
 * High-performance Tamil phonetic conversion & Tanglish search engine
 * Features:
 *  1. Zero-latency instant Tanglish & Phonetic Key pre-indexing
 *  2. Multi-word sentence token matching (AND logic across tokens, OR logic across fields)
 *  3. In-memory + LocalStorage 2-layer caching for API transliterations
 *  4. Debounced network transliteration fallback to https://translation.digitalmoitech.com/translate
 */

const TRANSLATE_API_URL = "https://translation.digitalmoitech.com/translate";

// Tamil Unicode character mappings
const TAMIL_VOWELS = {
  "\u0B85": "a",
  "\u0B86": "aa",
  "\u0B87": "i",
  "\u0B88": "ee",
  "\u0B89": "u",
  "\u0B8A": "oo",
  "\u0B8E": "e",
  "\u0B8F": "ae",
  "\u0B90": "ai",
  "\u0B92": "o",
  "\u0B93": "oa",
  "\u0B94": "au",
};

const TAMIL_CONSONANTS = {
  "\u0B95": "k",
  "\u0B99": "ng",
  "\u0B9A": "s",
  "\u0B9E": "nj",
  "\u0B9F": "t",
  "\u0BA3": "n",
  "\u0BA4": "th",
  "\u0BA8": "n",
  "\u0BA9": "n",
  "\u0BAA": "p",
  "\u0BAE": "m",
  "\u0BAF": "y",
  "\u0BB0": "r",
  "\u0BB1": "r",
  "\u0BB2": "l",
  "\u0BB3": "l",
  "\u0BB4": "zh",
  "\u0BB5": "v",
  "\u0BB6": "s",
  "\u0BB7": "sh",
  "\u0BB8": "s",
  "\u0BB9": "h",
  "\u0B9C": "j",
};

const TAMIL_VOWEL_SIGNS = {
  "\u0BBE": "aa",
  "\u0BBF": "i",
  "\u0BC0": "ee",
  "\u0BC1": "u",
  "\u0BC2": "oo",
  "\u0BC6": "e",
  "\u0BC7": "ae",
  "\u0BC8": "ai",
  "\u0BCA": "o",
  "\u0BCB": "oa",
  "\u0BCC": "au",
};

const PULLI = "\u0BCD"; // Virama

/**
 * Converts Tamil Unicode string into Romanized Tanglish representation
 */
export function tamilToTanglish(text) {
  if (!text) return "";
  let out = "";
  const chars = Array.from(text);
  for (let i = 0; i < chars.length; i++) {
    const c = chars[i];
    const next = chars[i + 1];

    if (TAMIL_VOWELS[c]) {
      out += TAMIL_VOWELS[c];
    } else if (TAMIL_CONSONANTS[c]) {
      const cons = TAMIL_CONSONANTS[c];
      if (next === PULLI) {
        out += cons;
        i++; // skip pulli
      } else if (next && TAMIL_VOWEL_SIGNS[next]) {
        out += cons + TAMIL_VOWEL_SIGNS[next];
        i++; // skip vowel sign
      } else {
        out += cons + "a";
      }
    } else {
      out += c;
    }
  }
  return out.toLowerCase();
}

/**
 * Splits a mixed Tamil+English word (e.g. "முத்vel", "செந்thil", "கார்த்திk")
 * into its components:
 * - leadingNonTamil (e.g. initials like "S.")
 * - tamilPrefix (e.g. "முத்")
 * - englishSuffix (e.g. "vel")
 */
export function splitMixedTamilWord(word) {
  if (!word || typeof word !== "string") return null;
  if (!/[\u0B80-\u0BFF]/.test(word) || !/[a-zA-Z]/.test(word)) {
    return null;
  }
  const standardMatch = word.match(/^([\u0B80-\u0BFF]+)([a-zA-Z0-9']+)$/);
  if (standardMatch) {
    return {
      leadingNonTamil: "",
      tamilPrefix: standardMatch[1],
      englishSuffix: standardMatch[2],
      isMixed: true,
    };
  }
  const flexibleMatch = word.match(/^(.*?)([\u0B80-\u0BFF]+)([a-zA-Z0-9']+)$/);
  if (flexibleMatch) {
    return {
      leadingNonTamil: flexibleMatch[1],
      tamilPrefix: flexibleMatch[2],
      englishSuffix: flexibleMatch[3],
      isMixed: true,
    };
  }
  return null;
}

/**
 * Converts a semi-finished Tamil prefix and English suffix into a joined Tanglish string.
 * Handles trailing inherent vowels / consonants to preserve phonetic harmony.
 * Example:
 *   "முத்" + "vel" -> "muthvel"
 *   "செந்" + "thil" -> "senthil"
 *   "கார்த்தி" + "k" -> "kaarththik"
 *   "மாரிமு" + "thu" -> "maarimuthu"
 *   "முருக" + "an" -> "murukan"
 */
export function getTanglishForMixedWord(tamilPrefix, englishSuffix) {
  if (!tamilPrefix) return englishSuffix || "";
  const tanglishPrefix = tamilToTanglish(tamilPrefix);
  if (!englishSuffix) return tanglishPrefix;

  if (tanglishPrefix.endsWith("a") && /^[aeiou]/i.test(englishSuffix)) {
    return tanglishPrefix.slice(0, -1) + englishSuffix;
  }
  return tanglishPrefix + englishSuffix;
}

/**
 * Normalizes a word into a canonical phonetic key.
 * Reduces common spelling variations (p/b, k/g/h, t/d/th, s/ch/j, repeated letters)
 * to allow fuzzy phonetic matches with zero performance penalty.
 */
export function toPhoneticKey(str) {
  if (!str) return "";
  let s = str.toLowerCase();

  // Multi-char digraph rules first
  s = s.replace(/tth|thth|th|dh/g, "t");
  s = s.replace(/cch|ch|sh|zh/g, "s");
  s = s.replace(/ng|nj/g, "n");
  s = s.replace(/aa/g, "a");
  s = s.replace(/ee|ii/g, "i");
  s = s.replace(/oo|uu/g, "u");
  s = s.replace(/ae|ea/g, "e");
  s = s.replace(/oa/g, "o");
  s = s.replace(/ai/g, "i");
  s = s.replace(/au/g, "u");

  // Single-char equivalents
  s = s.replace(/b/g, "p");
  s = s.replace(/d/g, "t");
  s = s.replace(/g/g, "k");
  s = s.replace(/h/g, "k");
  s = s.replace(/w/g, "v");
  s = s.replace(/z|j/g, "s");

  // Collapse consecutive duplicates (e.g. kk -> k, pp -> p, mm -> m)
  s = s.replace(/([a-z0-9])\1+/g, "$1");

  // Keep only alphanumeric
  s = s.replace(/[^a-z0-9]/g, "");
  return s;
}

/**
 * Pre-indexes a user object once for fast O(1) in-memory filtering.
 */
export function indexUserForSearch(u) {
  if (!u) return {};
  const tanglishName = tamilToTanglish(u.name || "");
  const tanglishFamily = tamilToTanglish(u.family_name || "");
  const tanglishOcc = tamilToTanglish(u.occupation || "");

  return {
    ...u,
    _rawLowerName: (u.name || "").toLowerCase(),
    _rawLowerFamily: (u.family_name || "").toLowerCase(),
    _rawLowerOcc: (u.occupation || "").toLowerCase(),
    _rawMobile: (u.mobile || "").trim(),
    _tanglishName: tanglishName,
    _tanglishFamily: tanglishFamily,
    _tanglishOcc: tanglishOcc,
    _phoneticName: toPhoneticKey(tanglishName),
    _phoneticFamily: toPhoneticKey(tanglishFamily),
    _phoneticOcc: toPhoneticKey(tanglishOcc),
  };
}

// ── In-Memory & LocalStorage Transliteration Cache ──────────────────────────────
const memoryTransliterationCache = new Map();

function initLocalCache() {
  try {
    const raw = localStorage.getItem("dwt_tamil_translations");
    if (raw) {
      const parsed = JSON.parse(raw);
      for (const [k, v] of Object.entries(parsed)) {
        memoryTransliterationCache.set(k, v);
      }
    }
  } catch (e) {}
}
initLocalCache();

function persistLocalCache(key, suggestions) {
  try {
    memoryTransliterationCache.set(key, suggestions);
    const raw = localStorage.getItem("dwt_tamil_translations");
    const parsed = raw ? JSON.parse(raw) : {};
    parsed[key] = suggestions;
    localStorage.setItem("dwt_tamil_translations", JSON.stringify(parsed));
  } catch (e) {}
}

/**
 * Get cached transliterations for a given English term
 */
export function getCachedTransliteration(term) {
  if (!term) return null;
  const key = term.trim().toLowerCase();
  return memoryTransliterationCache.get(key) || null;
}

/**
 * Asynchronously fetch transliterations from API with caching
 */
export async function fetchTransliteration(term) {
  if (!term || !term.trim()) return [];
  const key = term.trim().toLowerCase();

  const cached = getCachedTransliteration(key);
  if (cached) return cached;

  const mixed = splitMixedTamilWord(term.trim());
  if (mixed) {
    const { leadingNonTamil, tamilPrefix, englishSuffix } = mixed;
    const tanglishWord = getTanglishForMixedWord(tamilPrefix, englishSuffix);
    const rawMixed = tamilPrefix + englishSuffix;

    try {
      const [tanglishRes, rawRes] = await Promise.all([
        axios
          .post(
            TRANSLATE_API_URL,
            { text: tanglishWord, source: "en", target: "ta", mode: "transliterate" },
            { headers: { "Content-Type": "application/json" } }
          )
          .catch(() => null),
        axios
          .post(
            TRANSLATE_API_URL,
            { text: rawMixed, source: "en", target: "ta", mode: "transliterate" },
            { headers: { "Content-Type": "application/json" } }
          )
          .catch(() => null),
      ]);

      const tanglishList = [
        tanglishRes?.data?.translated,
        ...(Array.isArray(tanglishRes?.data?.suggestions) ? tanglishRes.data.suggestions : []),
      ].filter(Boolean);

      const rawTranslated = rawRes?.data?.translated;
      const rawSuggestions = Array.isArray(rawRes?.data?.suggestions)
        ? rawRes.data.suggestions
        : [];

      const prefixedRaw = [];
      if (rawTranslated && rawTranslated.startsWith(tamilPrefix)) {
        prefixedRaw.push(rawTranslated);
      }
      for (const s of rawSuggestions) {
        if (s.startsWith(tamilPrefix)) prefixedRaw.push(s);
        else prefixedRaw.push(tamilPrefix + s);
      }

      const tanglishPrefixMatches = tanglishList.filter((s) => s.startsWith(tamilPrefix));
      const otherTanglish = tanglishList.filter((s) => !s.startsWith(tamilPrefix));

      const combined = Array.from(
        new Set([...tanglishPrefixMatches, ...prefixedRaw, ...otherTanglish])
      ).filter(Boolean);

      const fullSuggestions = leadingNonTamil
        ? combined.map((s) => leadingNonTamil + s)
        : combined;

      if (fullSuggestions.length > 0) {
        persistLocalCache(key, fullSuggestions);
        return fullSuggestions;
      }
    } catch (err) {
      // Fail silently when offline or unreachable
    }
    return [];
  }

  try {
    const res = await axios.post(
      TRANSLATE_API_URL,
      { text: term.trim(), source: "en", target: "ta", mode: "transliterate" },
      { headers: { "Content-Type": "application/json" } }
    );

    const apiSuggestions = Array.isArray(res.data?.suggestions)
      ? res.data.suggestions
      : [];
    const translated = res.data?.translated;

    const combined = Array.from(
      new Set([translated, ...apiSuggestions].filter(Boolean))
    );

    if (combined.length > 0) {
      persistLocalCache(key, combined);
      return combined;
    }
  } catch (err) {
    // Fail silently when offline or unreachable
  }
  return [];
}

/**
 * Evaluates whether an indexed user matches a query.
 * Supports multi-word sentences (all words must match).
 *
 * @param {Object} indexedUser Pre-indexed user
 * @param {string} query Search input string
 * @param {Array<string>} extraTamilSuggestions Suggestions from API/cache
 * @returns {boolean}
 */
export function matchUserWithQuery(indexedUser, query, extraTamilSuggestions = []) {
  if (!query || !query.trim()) return true;
  const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);

  return tokens.every((token) => {
    const isEnglish = /[a-z0-9]/i.test(token);
    const tokenPhonetic = isEnglish ? toPhoneticKey(token) : "";

    // 1. Direct raw substring match (Tamil, English, Mobile, Occupation)
    if (
      indexedUser._rawLowerName.includes(token) ||
      indexedUser._rawLowerFamily.includes(token) ||
      indexedUser._rawLowerOcc.includes(token) ||
      indexedUser._rawMobile.includes(token)
    ) {
      return true;
    }

    // 2. Tanglish match
    if (
      isEnglish &&
      (indexedUser._tanglishName.includes(token) ||
        indexedUser._tanglishFamily.includes(token) ||
        indexedUser._tanglishOcc.includes(token))
    ) {
      return true;
    }

    // 3. Phonetic key match
    if (
      tokenPhonetic &&
      (indexedUser._phoneticName.includes(tokenPhonetic) ||
        indexedUser._phoneticFamily.includes(tokenPhonetic) ||
        indexedUser._phoneticOcc.includes(tokenPhonetic))
    ) {
      return true;
    }

    // 4. Extra Tamil suggestions match (from transliteration API / local cache)
    if (extraTamilSuggestions && extraTamilSuggestions.length > 0) {
      for (const s of extraTamilSuggestions) {
        if (!s) continue;
        const sLower = s.toLowerCase();
        if (
          indexedUser._rawLowerName.includes(sLower) ||
          indexedUser._rawLowerFamily.includes(sLower) ||
          indexedUser._rawLowerOcc.includes(sLower)
        ) {
          return true;
        }
      }
    }

    // 5. Check if token itself has cached transliteration
    const tokenCached = getCachedTransliteration(token);
    if (tokenCached && tokenCached.length > 0) {
      for (const s of tokenCached) {
        if (!s) continue;
        const sLower = s.toLowerCase();
        if (
          indexedUser._rawLowerName.includes(sLower) ||
          indexedUser._rawLowerFamily.includes(sLower) ||
          indexedUser._rawLowerOcc.includes(sLower)
        ) {
          return true;
        }
      }
    }

    return false;
  });
}

/**
 * Parses a full name into name and initial parts.
 * Handles:
 *  - Leading initials: "J. Ramesh", "J Ramesh", "J.K. Ramesh", "ஜெ. பொம்முராஜ்", "J.பொம்முராஜ்"
 *  - Trailing initials: "Ramesh J", "பொம்முராஜ் J", "பொம்முராஜ்.J", "பொம்முராஜ் J."
 */
export function parseNameAndInitial(fullName) {
  if (!fullName || typeof fullName !== "string") {
    return { name: "", initial: "" };
  }
  const trimmed = fullName.trim();
  if (!trimmed) {
    return { name: "", initial: "" };
  }

  // 1. Leading initial with space or dot
  const leadingMatch = trimmed.match(
    /^([A-Za-z\u0B80-\u0BFF]{1,4}(?:\.[A-Za-z\u0B80-\u0BFF]{1,4})*)\.?\s+(.+)$/
  );
  if (leadingMatch) {
    const initCandidate = leadingMatch[1].replace(/\.$/, "").trim();
    const nameCandidate = leadingMatch[2].trim();
    const strippedInit = initCandidate.replace(/\./g, "");
    if (
      strippedInit.length >= 1 &&
      strippedInit.length <= 4 &&
      !/^\d+$/.test(strippedInit) &&
      nameCandidate.length > 0
    ) {
      return {
        name: nameCandidate,
        initial: initCandidate.toUpperCase(),
      };
    }
  }

  // Leading initial with dot directly attached without space: "J.பொம்முராஜ்"
  const leadingDotMatch = trimmed.match(
    /^([A-Za-z\u0B80-\u0BFF]{1,3}(?:\.[A-Za-z\u0B80-\u0BFF]{1,3})*)\.([^\s.]+.*)$/
  );
  if (leadingDotMatch) {
    const initCandidate = leadingDotMatch[1].trim();
    const nameCandidate = leadingDotMatch[2].trim();
    const strippedInit = initCandidate.replace(/\./g, "");
    if (
      strippedInit.length >= 1 &&
      strippedInit.length <= 4 &&
      !/^\d+$/.test(strippedInit) &&
      nameCandidate.length > 0
    ) {
      return {
        name: nameCandidate,
        initial: initCandidate.toUpperCase(),
      };
    }
  }

  // 2. Trailing initial with dot attached without space: "பொம்முராஜ்.J"
  const trailingDotMatch = trimmed.match(
    /^(.+?)\.([A-Za-z\u0B80-\u0BFF]{1,3}(?:\.[A-Za-z\u0B80-\u0BFF]{1,3})*)\.?$/
  );
  if (trailingDotMatch) {
    const nameCandidate = trailingDotMatch[1].trim();
    const initCandidate = trailingDotMatch[2].replace(/\.$/, "").trim();
    const strippedInit = initCandidate.replace(/\./g, "");
    if (
      strippedInit.length >= 1 &&
      strippedInit.length <= 4 &&
      !/^\d+$/.test(strippedInit) &&
      nameCandidate.length > 0
    ) {
      return {
        name: nameCandidate,
        initial: initCandidate.toUpperCase(),
      };
    }
  }

  // Trailing initial with space: "பொம்முராஜ் J", "பொம்முராஜ் K."
  const parts = trimmed.split(/\s+/);
  if (parts.length > 1) {
    const lastPart = parts[parts.length - 1];
    const stripped = lastPart.replace(/\./g, "");
    if (
      stripped.length >= 1 &&
      stripped.length <= 4 &&
      !/^\d+$/.test(stripped)
    ) {
      return {
        name: parts.slice(0, -1).join(" "),
        initial: stripped.toUpperCase(),
      };
    }
  }

  return { name: trimmed, initial: "" };
}

/**
 * Combines name and initial for database persistence.
 */
export function getCombinedName(name, initial) {
  const cleanName = name ? String(name).trim() : "";
  const cleanInitial = initial ? String(initial).trim() : "";
  if (!cleanInitial) return cleanName;
  if (!cleanName) return cleanInitial;
  if (
    cleanName.endsWith(" " + cleanInitial) ||
    cleanName.startsWith(cleanInitial + " ") ||
    cleanName === cleanInitial
  ) {
    return cleanName;
  }
  return `${cleanName} ${cleanInitial}`;
}

/**
 * Pre-indexes a simple text item (e.g. village or area name) for instant phonetic search.
 * Supports string or object { village, village_en } / { text, alias }
 */
export function indexTextItemForSearch(item, extraAlias = "") {
  let text = "";
  let alias = extraAlias || "";

  if (typeof item === "string") {
    text = item.trim();
  } else if (item && typeof item === "object") {
    text = (item.village || item.name || item.area || item.text || "").trim();
    alias = (item.village_en || item.en || item.alias || extraAlias || "").trim();
  }

  const tanglish = tamilToTanglish(text);
  const tanglishAlias = alias ? tamilToTanglish(alias) : "";

  return {
    raw: text,
    alias: alias,
    _rawLower: text.toLowerCase(),
    _rawAliasLower: alias.toLowerCase(),
    _tanglish: tanglish,
    _tanglishAlias: tanglishAlias,
    _phonetic: toPhoneticKey(tanglish),
    _phoneticAlias: toPhoneticKey(tanglishAlias),
  };
}

/**
 * Matches an indexed text item against search tokens.
 */
export function matchTextItemWithQuery(indexedItem, query, extraTamilSuggestions = []) {
  if (!query || !query.trim()) return true;
  const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);

  return tokens.every((token) => {
    const isEnglish = /[a-z0-9]/i.test(token);
    const tokenPhonetic = isEnglish ? toPhoneticKey(token) : "";

    // 1. Direct raw substring match
    if (
      indexedItem._rawLower.includes(token) ||
      (indexedItem._rawAliasLower && indexedItem._rawAliasLower.includes(token))
    ) {
      return true;
    }

    // 2. Tanglish & Phonetic key match
    if (isEnglish) {
      if (
        indexedItem._tanglish.includes(token) ||
        (indexedItem._tanglishAlias && indexedItem._tanglishAlias.includes(token))
      ) {
        return true;
      }
      if (
        tokenPhonetic &&
        (indexedItem._phonetic.includes(tokenPhonetic) ||
          (indexedItem._phoneticAlias && indexedItem._phoneticAlias.includes(tokenPhonetic)))
      ) {
        return true;
      }
    }

    // 3. Extra transliterated suggestions from API/cache
    if (extraTamilSuggestions && extraTamilSuggestions.length > 0) {
      for (const sug of extraTamilSuggestions) {
        const lowerSug = String(sug).toLowerCase();
        if (
          indexedItem._rawLower.includes(lowerSug) ||
          lowerSug.includes(indexedItem._rawLower)
        ) {
          return true;
        }
      }
    }

    return false;
  });
}
